import React, { useState, useEffect, useContext } from 'react';
import { ArrowLeft } from 'lucide-react';
import api from '../../utils/api';
import { AuthContext } from '../../context/AuthContext';


const faqs = [
  {
    q: "My QR code is not scanning — what do I do?",
    a: (
      <>
        Tap the <strong>Refresh</strong> button next to your QR code to generate a new one.
        Make sure your screen brightness is high. If the issue continues, ask your gym owner
        to check your membership status.
        <div className="help-tip">★ Tip: Clean your screen before scanning for better detection.</div>
      </>
    ),
  },
  {
    q: "My check-in is not showing in history",
    a: "If the gym scanner was offline during your check-in, your data syncs automatically once the internet is restored. Wait a few minutes and refresh the app. All check-ins are saved and never lost.",
  },
  {
    q: "How do I earn points and rewards?",
    a: (
      <>
        Go to the <strong>Score</strong> tab and watch the daily ad. You earn 1 point per day.
        Points accumulate over the month and unlock rewards like free protein shakes, gym T-shirts,
        and discounts at month end.
        <div className="help-tip">★ You can only earn 1 point per day — watch the ad daily for maximum rewards.</div>
      </>
    ),
  },
  {
    q: "How do I redeem my reward?",
    a: (
      <>
        Once you have enough points, tap <strong>Redeem</strong> on the reward in the Score tab.
        Show the confirmation screen to your gym owner or trainer to claim your reward.
      </>
    ),
  },
  {
    q: "My membership expired — how do I renew?",
    a: "Contact your gym owner directly or visit the gym counter to renew. Once renewed, your QR code will automatically reactivate and your app will update within minutes.",
  },
  {
    q: "How does the 100-day challenge work?",
    a: (
      <>
        Every time you check in at the gym, your consistency score increases. Reach milestones at 30,
        60, and 100 days to unlock special rewards. Missing a day does not reset your score — it just
        slows your progress.
        <div className="help-tip">★ Check in every day even on rest days to maintain your streak.</div>
      </>
    ),
  },
  {
    q: "How do I update my fitness measurements?",
    a: (
      <>
        Go to the <strong>Health</strong> tab and tap Add Measurements to log your own weight and body
        measurements. If you have a personal trainer, they can also update your measurements directly
        from their FitScan trainer app — it reflects on your Health tab automatically.
      </>
    ),
  },
];

const HelpScreen = ({ onBack, gymCode: propGymCode }) => {
  const { user: authUser } = useContext(AuthContext);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [videoUrl, setVideoUrl] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  useEffect(() => {
    const fetchWalkthroughVideo = async () => {
      let gymcode = propGymCode;

      if (!gymcode) {
        gymcode = authUser?.gymcode;
      }

      if (!gymcode) {
        try {
          const localUser = JSON.parse(localStorage.getItem('fitscan_user') || '{}');
          gymcode = localUser.gymcode || localUser.gymCode;
        } catch (e) { }
      }

      // If still not found, check if user is an owner/trainer and fetch their gym
      if (!gymcode) {
        try {
          const profileRes = await api.get('/api/profile/details');
          gymcode = profileRes.data?.gymCode || profileRes.data?.gymcode;
        } catch (e) { }
      }

      if (!gymcode) return;

      try {
        const response = await api.post('/api/Gym/gymfeed', { gymcode });
        const posts = response.data?.posts || [];

        // Find post with title "Instrutions" or "Instructions" (case-insensitive, fuzzy)
        // or fallback to any post with a Cloudinary video URL
        const videoPost = posts.find(p => {
          const t = (p.title || '').toLowerCase();
          return t.includes('instrution') || t.includes('instruction');
        }) || posts.find(p => p.imageUrl && p.imageUrl.includes('player.cloudinary.com'));

        if (videoPost && videoPost.imageUrl) {
          setVideoUrl(videoPost.imageUrl);
        }
      } catch (err) {
        console.error('Error fetching help video from gym feed:', err);
      }
    };

    fetchWalkthroughVideo();
  }, [authUser, propGymCode]);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      alert('Please describe your issue before submitting.');
      return;
    }
    setSubmitting(true);
    setMsg('');
    try {
      const response = await api.post('/api/UserReport', { description });
      if (response.status === 200) {
        setMsg('Report submitted successfully!');
        setDescription('');
        setTimeout(() => {
          setShowReportForm(false);
          setMsg('');
        }, 3000);
      } else {
        setMsg('Failed to submit report. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setMsg('An error occurred while submitting.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/919XXXXXXXXX', '_blank');
  };

  return (
    <div className="max-w-[480px] mx-auto pt-4 pb-8">
      {/* Top Bar */}
      <div className="flex items-center gap-3 mb-6">
        <button
          className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all"
          onClick={onBack}
        >
          <ArrowLeft size={18} />
        </button>
        <h2 className="text-2xl font-bold text-white">Help & FAQ</h2>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2.5 bg-[#181818] border-[0.5px] border-[#2b2b2b] rounded-[12px] p-2.5 px-3.5 mb-6">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="7" cy="7" r="5" stroke="#888" strokeWidth="1.5" />
          <path d="M11 11l3 3" stroke="#888" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          placeholder="Search for help..."
          className="bg-transparent border-none outline-none text-[14px] text-white w-full placeholder:text-[#888]"
        />
      </div>

      {/* FAQ Section */}
      <div className="text-[11px] font-medium text-[#888] uppercase tracking-[0.6px] mb-2">Frequently asked questions</div>
      <div className="bg-[#101010] border-[0.5px] border-[#2b2b2b] rounded-[12px] overflow-hidden mb-5">
        {faqs.map((faq, index) => (
          <div className="border-b-[0.5px] border-[#2b2b2b] last:border-b-0" key={index}>
            <div
              className="flex items-center justify-between p-3.5 cursor-pointer gap-2.5 transition-colors hover:bg-[#181818]"
              onClick={() => toggleFaq(index)}
            >
              <span className="text-[13px] font-medium text-white leading-relaxed">{faq.q}</span>
              <span className={`text-[12px] transition-transform shrink-0 ${openFaqIndex === index ? 'rotate-180 text-[#6CD800]' : 'text-[#888]'}`}>▼</span>
            </div>
            <div className={`overflow-hidden transition-all duration-300 ${openFaqIndex === index ? 'max-h-[300px] p-3.5 pt-2.5 border-t-[0.5px] border-[#2b2b2b]' : 'max-h-0 p-0 px-3.5 opacity-0'}`}>
              <div className="text-[13px] text-[#aaa] leading-relaxed">
                {faq.a}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Support Section */}
      <div className="text-[11px] font-medium text-[#888] uppercase tracking-[0.6px] mb-2">Contact &amp; support</div>
      <div className="bg-[#101010] border-[0.5px] border-[#2b2b2b] rounded-[12px] overflow-hidden mb-5">
        <div className="flex items-center gap-3 p-3.5 border-b-[0.5px] border-[#2b2b2b] cursor-pointer transition-colors hover:bg-[#181818]" onClick={handleWhatsApp}>
          <div className="w-9 h-9 rounded-[10px] flex items-center justify-center text-[16px] shrink-0 bg-[#25D366]/15">💬</div>
          <div className="flex flex-col">
            <div className="text-[13px] font-medium text-white">Chat with us on WhatsApp</div>
            <div className="text-[11px] text-[#888] mt-0.5">Usually replies within 1 hour</div>
          </div>
          <span className="ml-auto text-[12px] text-[#888]">›</span>
        </div>

        <div className="flex items-center gap-3 p-3.5 border-b-[0.5px] border-[#2b2b2b] cursor-pointer transition-colors hover:bg-[#181818]" onClick={() => setShowReportForm(!showReportForm)}>
          <div className="w-9 h-9 rounded-[10px] flex items-center justify-center text-[16px] shrink-0 bg-[#181818]">🐛</div>
          <div className="flex flex-col">
            <div className="text-[13px] font-medium text-white">Report a problem</div>
            <div className="text-[11px] text-[#888] mt-0.5">Tell us what went wrong</div>
          </div>
          <span className="ml-auto text-[12px] text-[#888]">›</span>
        </div>

        <div className={`overflow-hidden transition-all duration-300 px-3.5 ${showReportForm ? 'max-h-[280px] py-3.5 pt-2.5 border-t-[0.5px] border-[#2b2b2b]' : 'max-h-0'}`}>
          {msg && (
            <div className={`text-xs p-2 rounded mb-2 font-medium ${msg.includes('successfully') ? 'bg-[#6CD800]/10 text-[#6CD800] border border-[#6CD800]/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
              {msg}
            </div>
          )}
          <textarea
            placeholder="Describe your issue in detail — what happened, which screen, what you expected..."
            className="w-full bg-[#181818] border-[0.5px] border-[#2b2b2b] rounded-[8px] p-2.5 text-[13px] text-white resize-none h-20 mt-1 font-inherit placeholder:text-[#888] outline-none focus:border-[#6CD800] transition-colors"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
          />
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="mt-2 w-full bg-[#6CD800] hover:bg-[#6CD800]/95 text-black border-none rounded-[8px] p-2.5 text-[13px] font-bold cursor-pointer transition-all disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>

        <div
          className="flex items-center gap-3 p-3.5 last:border-b-0 cursor-pointer transition-colors hover:bg-[#181818]"
          onClick={() => {
            if (videoUrl) {
              setShowVideoModal(true);
            } else {
              alert("Walkthrough video titled 'Instrutions' could not be found in the gym feed.");
            }
          }}
        >
          <div className="w-9 h-9 rounded-[10px] flex items-center justify-center text-[16px] shrink-0 bg-[#6CD800]/10">▶</div>
          <div className="flex flex-col">
            <div className="text-[13px] font-medium text-white">How to use FitScan</div>
            <div className="text-[11px] text-[#888] mt-0.5">Quick video walkthrough</div>
          </div>
          <span className="ml-auto text-[12px] text-[#888]">›</span>
        </div>
      </div>

      {/* Video Walkthrough Modal */}
      {showVideoModal && (
        <div
          className="fixed inset-0 bg-black/85 z-[100] flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setShowVideoModal(false)}
        >
          <div
            className="bg-[#101010] border border-[#2b2b2b] rounded-2xl overflow-hidden w-full max-w-xl shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#2b2b2b]">
              <span className="text-sm font-bold text-white uppercase tracking-wider">How to use FitScan</span>
              <button
                className="w-8 h-8 rounded-full bg-[#181818] border border-[#2b2b2b] flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                onClick={() => setShowVideoModal(false)}
              >
                ✕
              </button>
            </div>
            {/* Video container */}
            <div className="aspect-video bg-black relative">
              <iframe
                src={videoUrl}
                className="w-full h-full"
                allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                allowFullScreen
                title="Walkthrough Video"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpScreen;
